import type { Session } from "next-auth";

import {
	backendUserToSavedLoginAccount,
	saveCurrentAccountIfEligible,
	upsertSavedLoginAccount,
	type SavedLoginAccount,
} from "@/lib/auth/saved-accounts";

export type AccountSwitchResult =
	| { status: "success"; idToken: string; user: Record<string, unknown> }
	| { status: "requires2FA" }
	| { status: "unauthorized" }
	| { status: "error"; message: string };

type SwitchOptions = {
	twoFactorToken?: string;
};

function getBackendUrl() {
	return (process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:3001").replace(/\/$/, "");
}

/** Park the active server session so it survives inactivity while on another account. */
export async function parkSessionForAccountSwitcher(session: Session | null): Promise<boolean> {
	const idToken = session?.idToken?.trim();
	if (!idToken || typeof window === "undefined") return false;

	try {
		const response = await fetch(`${getBackendUrl()}/auth/park-switcher-session`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${idToken}`,
				"Content-Type": "application/json",
			},
		});
		if (!response.ok) return false;
		const data = await response.json().catch(() => ({}));
		const parked = data?.parked === true;
		if (parked) {
			saveCurrentAccountIfEligible(session);
		}
		return parked;
	} catch {
		return false;
	}
}

async function syncCurrentAccountToken(session: Session | null) {
	if (!session?.user?.id || !session.idToken) return;
	saveCurrentAccountIfEligible(session);
}

/** Apply the switched account via NextAuth so the browser cookie actually updates. */
async function establishNextAuthSession(
	idToken: string,
	user: Record<string, unknown>,
): Promise<boolean> {
	const { signIn, getSession } = await import("next-auth/react");
	const userId = String(user.id ?? user._id ?? "").trim();
	if (!userId) return false;

	const sessionUser = { ...user, id: userId };
	const signInResult = await signIn("credentials", {
		redirect: false,
		establishSession: "true",
		idToken,
		user: JSON.stringify(sessionUser),
	});

	if (!signInResult?.ok) {
		return false;
	}

	for (let attempt = 0; attempt < 15; attempt += 1) {
		const session = await getSession();
		if (session?.idToken?.trim() && session.user?.id === userId) {
			return true;
		}
		await new Promise((resolve) => setTimeout(resolve, 100));
	}

	return false;
}

export async function executeAccountSwitch(
	account: SavedLoginAccount,
	currentSession: Session | null,
	options?: SwitchOptions,
): Promise<AccountSwitchResult> {
	const { getSession } = await import("next-auth/react");
	const liveSession = (await getSession()) ?? currentSession;
	await syncCurrentAccountToken(liveSession);
	await parkSessionForAccountSwitcher(liveSession);

	const response = await fetch("/api/auth/create-session", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			idToken: account.idToken,
			user: account.user ?? {
				id: account.userId,
				email: account.email,
				firstName: account.firstName,
				lastName: account.lastName,
				avatar: account.avatar,
				role: account.role,
			},
			twoFactorToken: options?.twoFactorToken,
		}),
	});

	let payload: Record<string, unknown> = {};
	try {
		payload = await response.json();
	} catch {
		// ignore
	}

	if (payload.requires2FA === true) {
		return { status: "requires2FA" };
	}

	if (!response.ok) {
		const message =
			typeof payload.error === "string"
				? payload.error
				: "Could not switch account. Please try again.";
		if (response.status === 401 && !options?.twoFactorToken) {
			return { status: "unauthorized" };
		}
		return { status: "error", message };
	}

	const idToken = String(payload.idToken ?? "").trim();
	const user = payload.user as Record<string, unknown> | undefined;
	if (!idToken || !user) {
		return { status: "error", message: "Invalid session response from server." };
	}

	const sessionEstablished = await establishNextAuthSession(idToken, user);
	if (!sessionEstablished) {
		return {
			status: "error",
			message: "Account switched on server but browser session was not updated. Please try again.",
		};
	}

	upsertSavedLoginAccount(backendUserToSavedLoginAccount(user, idToken));
	return { status: "success", idToken, user };
}
